import { useMemo } from 'react';
import { useGetNotesQuery, NoteFilter, NoteTypeEnum } from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';

// Dummy notes for preview mode
const dummyNotes = [
  {
    id: '1',
    title: 'Meeting Follow-up',
    content: 'Discussed project timeline and next steps',
    date: 'Mar 10, 2026',
    type: 'manual' as const,
  },
  {
    id: '2',
    title: 'Product Demo Feedback',
    content: 'Customer was impressed with new features',
    date: 'Mar 8, 2026',
    type: 'manual' as const,
  },
];

interface UseContactNotesOptions {
  skip?: boolean; // Skip auto-fetch (e.g., when parent component is not visible)
}

/**
 * Hook for fetching notes associated with a contact
 * Supports Lovable Preview mode with dummy data
 */
export const useContactNotes = (contactId: string, options: UseContactNotesOptions = {}) => {
  const { skip = false } = options;
  const workspaceId = useWorkspaceId();

  const filter: NoteFilter = useMemo(
    () => ({
      workspaceId: workspaceId || undefined,
      contactId: contactId, // singular, not array
      deleted: false,
    }),
    [workspaceId, contactId]
  );

  const { data, loading, error, refetch } = useGetNotesQuery({
    variables: {
      filter,
      first: 100, // Load all notes for now
    },
    skip: skip || isLovablePreview() || !workspaceId || !contactId,
    fetchPolicy: 'cache-and-network',
  });

  const notes = useMemo(() => {
    if (isLovablePreview()) {
      return dummyNotes;
    }

    return (
      data?.notes?.edges
        // Tasks are notes with type=task and render in the contact's separate
        // Tasks tab — drop them here so a task doesn't appear in both places.
        // Filter client-side (only explicit tasks) rather than adding a
        // type=note server filter, which would also hide legacy notes whose
        // `type` isn't indexed. Mirrors use-deal-notes.
        ?.filter((edge) => edge.node?.type !== NoteTypeEnum.Task)
        .map((edge) => ({
          id: edge.node.id,
          title: edge.node.title || 'Untitled Note',
          content: (edge.node as any).blocks || '',
          date: edge.node.createdAt || '',
          type: edge.node.aiGenerated ? ('ai' as const) : ('manual' as const),
        })) || []
    );
  }, [data]);

  return {
    notes,
    loading: isLovablePreview() ? false : loading,
    error: isLovablePreview() ? null : error,
    refetch: isLovablePreview() ? (() => Promise.resolve({} as ReturnType<typeof refetch>)) : refetch,
  };
};
